import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Users, RefreshCw, MapPin, Clock, AlertCircle, Building2, ShieldAlert } from 'lucide-react';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const workerIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#E8613A;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3)">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -34],
});

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [positions, map]);
  return null;
}

function timeSince(isoStr) {
  if (!isoStr) return '';
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return 'just nu';
  if (diff < 3600) return `${Math.floor(diff / 60)} min sedan`;
  return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m sedan`;
}

function clockedHours(clockIn) {
  if (!clockIn) return '0h 0m';
  const diff = (Date.now() - new Date(clockIn).getTime()) / 1000;
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function SuperAdminActivityMap() {
  const [workers, setWorkers] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchActivity = useCallback(async () => {
    try {
      const [actRes, geoRes] = await Promise.all([
        api.getLiveActivity(),
        api.getAllGeofences().catch(() => ({ data: [] })),
      ]);
      setWorkers(actRes.data);
      setGeofences(geoRes.data || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch live activity:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchActivity, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchActivity]);

  const workersWithLocation = workers.filter(w => w.location?.latitude && w.location?.longitude);
  const workersWithoutLocation = workers.filter(w => !w.location?.latitude || !w.location?.longitude);
  const mapPositions = workersWithLocation.map(w => ({ lat: w.location.latitude, lng: w.location.longitude }));

  const defaultCenter = [59.3293, 18.0686]; // Stockholm

  return (
    <div className="space-y-6" data-testid="superadmin-activity-map">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Live Aktivitetskarta</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {workers.length} aktiva arbetare just nu
            {lastRefresh && <span className="ml-2">Uppdaterad: {lastRefresh.toLocaleTimeString('sv-SE')}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer" data-testid="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              className="rounded border-border"
            />
            Auto-uppdatera (15s)
          </label>
          <button
            onClick={fetchActivity}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-[#E8613A] text-white text-sm font-medium rounded-lg hover:bg-[#d4552f] transition-colors disabled:opacity-50"
            data-testid="refresh-activity-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Uppdatera
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map */}
        <div className="lg:col-span-3 bg-card border border-border rounded-xl overflow-hidden shadow-sm" style={{ height: 520 }}>
          {loading && workers.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <RefreshCw size={20} className="animate-spin mr-2" /> Laddar karta...
            </div>
          ) : (
            <MapContainer
              center={mapPositions.length > 0 ? [mapPositions[0].lat, mapPositions[0].lng] : defaultCenter}
              zoom={7}
              style={{ height: '100%', width: '100%' }}
              data-testid="activity-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {mapPositions.length > 0 && <FitBounds positions={mapPositions} />}
              {/* Geofence circles */}
              {geofences.map(gf => gf.geofence && (
                <Circle
                  key={`gf-${gf.id}`}
                  center={[gf.geofence.latitude, gf.geofence.longitude]}
                  radius={gf.geofence.radius_meters}
                  pathOptions={{
                    color: '#3b82f6',
                    fillColor: '#3b82f6',
                    fillOpacity: 0.08,
                    weight: 2,
                    dashArray: '6 4',
                  }}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="font-bold">{gf.name}</p>
                      <p className="text-gray-600">Radie: {gf.geofence.radius_meters}m</p>
                    </div>
                  </Popup>
                </Circle>
              ))}
              {workersWithLocation.map(worker => (
                <Marker
                  key={worker.user_id}
                  position={[worker.location.latitude, worker.location.longitude]}
                  icon={workerIcon}
                >
                  <Popup>
                    <div className="text-sm min-w-[180px]">
                      <p className="font-bold text-base mb-1">{worker.user_name}</p>
                      {worker.role_title && <p className="text-gray-600">{worker.role_title}</p>}
                      {worker.company_name && (
                        <p className="flex items-center gap-1 text-gray-600 mt-1">
                          <Building2 size={12} /> {worker.company_name}
                        </p>
                      )}
                      {worker.project_name && (
                        <p className="flex items-center gap-1 text-gray-600 mt-1">
                          <MapPin size={12} /> {worker.project_name}
                        </p>
                      )}
                      <p className="flex items-center gap-1 text-gray-600 mt-1">
                        <Clock size={12} /> Instämplad: {clockedHours(worker.clock_in)}
                      </p>
                      {worker.location.updated_at && (
                        <p className="text-muted-foreground text-xs mt-1">GPS: {timeSince(worker.location.updated_at)}</p>
                      )}
                      {worker.geofence_warning && (
                        <p className="flex items-center gap-1 text-red-600 text-xs mt-1 font-medium">
                          <ShieldAlert size={12} /> Utanför geofence ({worker.geofence_distance}m)
                        </p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>

        {/* Sidebar list */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-sm overflow-y-auto" style={{ maxHeight: 520 }}>
          <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Users size={16} className="text-[#E8613A]" />
            Aktiva arbetare ({workers.length})
          </h2>

          {workers.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">
              <Users size={32} className="mx-auto mb-2 opacity-40" />
              Inga aktiva arbetare just nu
            </div>
          ) : (
            <div className="space-y-2">
              {workersWithLocation.map(worker => (
                <div
                  key={worker.user_id}
                  className={`p-3 rounded-lg bg-background border transition-colors ${
                    worker.geofence_warning
                      ? 'border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-950/20'
                      : 'border-border hover:border-[#E8613A]/30'
                  }`}
                  data-testid={`worker-card-${worker.user_id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-2 h-2 rounded-full ${worker.geofence_warning ? 'bg-red-500' : 'bg-green-500 animate-pulse'}`} />
                    <span className="font-medium text-sm text-foreground truncate">{worker.user_name}</span>
                    {worker.geofence_warning && <ShieldAlert size={12} className="text-red-500 flex-shrink-0" />}
                  </div>
                  {worker.company_name && (
                    <p className="text-xs text-muted-foreground pl-4">{worker.company_name}</p>
                  )}
                  {worker.project_name && (
                    <p className="text-xs text-muted-foreground pl-4 flex items-center gap-1">
                      <MapPin size={10} /> {worker.project_name}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground pl-4 flex items-center gap-1">
                    <Clock size={10} /> {clockedHours(worker.clock_in)}
                  </p>
                  {worker.geofence_warning && (
                    <p className="text-xs text-red-600 dark:text-red-400 pl-4 mt-0.5">
                      Utanför zon ({worker.geofence_distance}m)
                    </p>
                  )}
                </div>
              ))}

              {workersWithoutLocation.length > 0 && (
                <>
                  <div className="flex items-center gap-2 mt-4 mb-2">
                    <AlertCircle size={14} className="text-amber-500" />
                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                      Utan GPS ({workersWithoutLocation.length})
                    </span>
                  </div>
                  {workersWithoutLocation.map(worker => (
                    <div
                      key={worker.user_id}
                      className="p-3 rounded-lg bg-background border border-dashed border-amber-300 dark:border-amber-700"
                      data-testid={`worker-card-no-gps-${worker.user_id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 rounded-full bg-amber-500" />
                        <span className="font-medium text-sm text-foreground truncate">{worker.user_name}</span>
                      </div>
                      {worker.company_name && (
                        <p className="text-xs text-muted-foreground pl-4">{worker.company_name}</p>
                      )}
                      {worker.project_name && (
                        <p className="text-xs text-muted-foreground pl-4">{worker.project_name}</p>
                      )}
                      <p className="text-xs text-amber-600 dark:text-amber-400 pl-4">GPS ej tillgänglig</p>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
